import styled from "@emotion/styled";
import type { BaseStepPopoverAssetWithIconRowProps } from "@stepfinance/react";
import {
  BaseStepPopoverAssetWithIconRow,
  BaseStepTabs,
  darkGrey2,
  darkGrey4,
  ExtraBoldText,
  lightGreen,
  PLUS_JAKARTA_SANS,
  redTag,
  SPACE_MONO,
  StepAssetBreakdownPopoverChip,
  StepButton,
  TagInfo,
  transparentWhite,
  white,
} from "@stepfinance/react";
import type { CollapseProps } from "antd";
import { Button as AntButton, Steps, Tabs, Tooltip } from "antd";
import type { FC } from "react";
import React from "react";
import Countdown from "react-countdown";

import { ReactComponent as DangerSVG } from "../assets/danger.svg";
import { ReactComponent as Chevron } from "../assets/selection-chevron.svg";
import { useAppConfig } from "../contexts/app/config";
import { useLanguageContext } from "../contexts/language/language";
import { getTokenIcon } from "../utils/tokenInfo";
import { countdownRenderString, localeDateYearFormatter, localeTimeFormatter } from "../utils/utils";
import { ExplorerLink } from "./ExplorerLink/ExplorerLink";

const ChevronWrapper = styled.div<{ isActive?: boolean }>`
  transform: ${({ isActive }) => (isActive ? "matrix(1, 0, 0, -1, 0, 0)" : undefined)};
  margin-top: ${({ isActive }) => (isActive ? undefined : "5px")};
  margin-right: 10px;
`;

const MenuChevronWrapper = styled.div<{ isActive?: boolean; isFlipped?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  transform: ${({ isActive, isFlipped }) => {
    if ((isActive && !isFlipped) || (!isActive && isFlipped)) {
      return "rotate(180deg)";
    } else {
      return "rotate(0deg)";
    }
  }};
`;

const ChevronWrapperLeft = styled.div<{ isActive?: boolean }>`
  transform: ${({ isActive }) => (isActive ? "rotate(0deg)" : "rotate(-90deg)")};
  margin-top: ${({ isActive }) => (isActive ? "2px" : undefined)};
`;

export const getExpandIcon: CollapseProps["expandIcon"] = ({ isActive }) => {
  return (
    <div>
      <ChevronWrapper isActive={isActive}>
        <Chevron />
      </ChevronWrapper>
    </div>
  );
};

export const getMenuExpandIcon = (isOpen?: boolean, isFlipped = false) => {
  return (
    <MenuChevronWrapper isFlipped={isFlipped} isActive={isOpen}>
      <Chevron />
    </MenuChevronWrapper>
  );
};

export const getExpandIconLeft: CollapseProps["expandIcon"] = ({ isActive }) => {
  return (
    <div>
      <ChevronWrapperLeft isActive={isActive}>
        <Chevron />
      </ChevronWrapperLeft>
    </div>
  );
};

export const StepStepper = styled(Steps)`
  &.ant-steps-small {
    .ant-steps-item {
      &.ant-steps-item-finish {
        .ant-steps-item-icon {
          .ant-steps-icon {
            margin-top: 2px;
            font-size: 11px;
          }
        }
      }
    }
  }
  .ant-steps-item {
    &.ant-steps-item-finish {
      color: ${white};
      .ant-steps-item-icon {
        background-color: ${darkGrey2};
        border-color: ${lightGreen};
        .ant-steps-icon {
          color: ${lightGreen};
        }
      }
      .ant-steps-item-content {
        .ant-steps-item-title {
          color: ${white};
        }
      }
      .ant-steps-item-tail::after {
        background-color: ${lightGreen};
      }
    }
    &.ant-steps-item-wait {
      color: ${transparentWhite};
      .ant-steps-item-icon {
        background-color: ${darkGrey2};
        border-color: ${darkGrey4};
        .ant-steps-icon {
          color: ${transparentWhite};
        }
      }
      .ant-steps-item-content {
        .ant-steps-item-title {
          color: ${transparentWhite};
        }
      }
    }
    &.ant-steps-item-active {
      .ant-steps-item-icon {
        background-color: ${lightGreen};
        border-color: ${lightGreen};
        .ant-steps-icon {
          color: ${darkGrey2};
        }
      }
      .ant-steps-item-content {
        .ant-steps-item-title {
          color: ${white};
        }
      }
    }
    .ant-steps-item-container {
      font-weight: bold;
      padding-bottom: 8px;
    }
    .ant-steps-item-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
    }
  }
`;

export const StepSolidButton = styled(AntButton)<{ icon?: React.ReactNode }>`
  border-radius: 8px;
  color: white;
  background-color: #2f2f2f;
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row-reverse;
  font-family: ${PLUS_JAKARTA_SANS}, serif;
  font-weight: 700;
  height: 42px;
  transition: 0s;
  ${(props) =>
    !!props.icon &&
    `
    svg {
      margin-left: 10px;
    }
    `}

  &:focus {
    background-color: #2f2f2f;
    color: white;
  }

  &:hover {
    background-color: #2f2f2f;
    color: rgba(6, 214, 160, 0.75);
    transition: 0.3s;
    svg path {
      stroke: rgba(6, 214, 160, 0.75);
      transition: 0.3s;
    }
  }
`;

export const StepModalButton = styled(StepButton)`
  border-radius: 8px;
  padding: 10px;
  width: 100%;
  height: 60px;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0;
`;

/**
 * Reusable Tabs component
 * Use to wrap <StepTab /> items
 */
export const StepTabs = BaseStepTabs;

/**
 * Convenience export for auto-complete
 */
export const StepTab = Tabs.TabPane;

type StepPopoverAssetWithIconRowProps = Omit<
  BaseStepPopoverAssetWithIconRowProps,
  "hideableWrapperFunction" | "getTokenIcon"
>;

/**
 * Asset row featuring the tokens' icon
 */
export const StepPopoverAssetWithIconRow: FC<StepPopoverAssetWithIconRowProps> = (props) => {
  const { hideableValueWrapper } = useAppConfig();
  return (
    <BaseStepPopoverAssetWithIconRow
      getTokenIcon={getTokenIcon}
      hideableWrapperFunction={hideableValueWrapper}
      {...props}
    />
  );
};

interface IconLinkProps {
  href: string;
  color?: string;
  children: React.ReactNode;
}

export const IconLink: FC<IconLinkProps> = ({ href, children, color = lightGreen }) => {
  return (
    <StyledAnchor href={href} target="_blank" rel="noopener noreferrer" color={color}>
      {children}
    </StyledAnchor>
  );
};

/**
 * Expiry Popover: Popover with details on the expiration of a product. Takes a timestamp as prop
 */
export const ExpiryPopover: FC<{ expiryTimeStamp: number }> = ({ expiryTimeStamp }) => {
  const {
    currentLanguageData: {
      common: { expiry },
    },
  } = useLanguageContext();
  return (
    <ExpiryPopoverWrapper>
      <StepAssetBreakdownPopoverChip
        title={<ExtraBoldText>{expiry.expiryDetails}</ExtraBoldText>}
        content={
          <>
            <VaultPopoverSection>
              <BoldText>{expiry.expiringOn}</BoldText>
              <ExpiryFullDate>
                <MonospacedText>
                  {localeDateYearFormatter.format(expiryTimeStamp)} @ {localeTimeFormatter.format(expiryTimeStamp)}
                </MonospacedText>
              </ExpiryFullDate>
            </VaultPopoverSection>
            <VaultPopoverSection>
              <BoldText>{expiry.expiringIn}</BoldText>
              <Countdown
                date={expiryTimeStamp}
                renderer={(args) => <MonospacedText>{countdownRenderString(args)}</MonospacedText>}
              ></Countdown>
            </VaultPopoverSection>
          </>
        }
        assetValue={1}
      />
    </ExpiryPopoverWrapper>
  );
};

export const ModalImageContainer = styled.div`
  width: 100%;

  img {
    max-width: 100%;
    max-height: inherit;
    margin: auto;
  }
`;

const ExpiryPopoverWrapper = styled.div`
  align-items: center;
  display: flex;
  margin-left: 0.5rem;

  .step-popover-chip {
    display: flex;
  }
`;

const ExpiryFullDate = styled.div`
  margin-left: 1rem;
`;

export const APRBreakdownInfo = styled.div<{ width: number | string }>`
  min-width: ${(props) => (typeof props.width === "number" ? `${props.width}px` : props.width)};
  div {
    display: flex;
    justify-content: space-between;
  }
  > div {
    margin-top: 5px;
  }
  .ant-divider {
    margin: 10px 0 !important;
  }
`;

/**
 * Used to create a semi-fake link that should use `navigate` inside of `onClick` instead of a `href` anchor for navigation with this new routing.
 */
export const NavigationLink = styled.a`
  color: #00f8b7;
  display: inline-flex;
  transition: color 0.3s;

  &:hover {
    cursor: pointer;
  }
`;

const StyledAnchor = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(props) => props.color};
`;

export const BoldText = styled.span`
  font-weight: 500;
`;

export const StepToolTipTitle = styled.div`
  min-height: 32px;
  min-width: 177px;
  padding: 5px 16px 4px;
  border-bottom: 1px solid #303030;
`;

export const StepPopoverInnerContent = styled.div`
  padding: 5px 16px 4px;
`;

/**
 * Use to wrap an Icon + TokenIconText combination when used in a table, e.g.
 * <IconTextContainer>
 *   <TokenIcon />
 *   <TokenIconText />
 * </IconTextContainer>
 */
export const IconTextContainer = styled.div`
  display: flex;
  align-items: center;
`;

/**
 * Accompanies a TokenIcon, intended to be perfectly vertically centered vs. the icon
 */
export const TokenIconText = styled.span<{ bold?: boolean; withoutIcon?: boolean }>`
  line-height: 28px;
  margin-left: ${(props) => (props.withoutIcon ? "0px" : "10px")};
  font-weight: ${(props) => (props.bold ? "bold" : "normal")};
`;

export const TransparentWhiteText = styled.span`
  color: #7d7d7d;
`;

export const MonospacedText = styled.span`
  font-family: Space Mono, monospace;
`;

export const TokenAmount = styled.span`
  font-family: Space Mono;
  margin-left: 10px;
`;

export const DropdownOverlayContainer = styled.div`
  max-width: 300px;
  padding: 10px;
  border-radius: 8px;
  background: #2f2f2f;
`;

/**
 * To display risks to platforms we don't trust
 */
export const DangerousPlatformTag: FC<{ platform: string }> = ({ platform }) => {
  const larixRisk =
    "Larix arbitrarily suspends deposits without notice. Your positions may be at increased risk of liquidation.";

  return platform.includes("Larix") ? (
    <span>
      {platform}
      {"  "}
      <TagInfo
        tagType={"text"}
        icon={<DangerSVG style={{ height: "13px" }} />}
        tagColorOverride={redTag}
        useSpaceMono={false}
        uppercase={false}
      >
        <Tooltip title={larixRisk} placement="bottom">
          <span>High Risk</span>
        </Tooltip>
      </TagInfo>
    </span>
  ) : (
    <span>{platform}</span>
  );
};

/**
 * Accompanies a TokenIcon or PoolIcon, intended to be perfectly vertically centered vs. the icon
 */
export const TokenIconLink = styled.a<{ bold?: boolean }>`
  line-height: 28px;
  margin-left: 10px;
  font-weight: ${(props) => (props.bold ? "bold" : "normal")};
`;

/**
 * Specifically renders a Solana Block explorer link via the address property
 */
export const TokenIconExplorerLink = styled(ExplorerLink)`
  line-height: 28px;
`;

export const VaultPopoverContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 320px;
  color: ${white};
`;

export const VaultPopoverSection = styled.div`
  display: flex;
  justify-content: space-between;

  &:not(:last-child) {
    padding-bottom: 18px;
  }
`;

export const NumberLabel = styled.span`
  font-family: "Space Mono";
  font-size: 14px;
`;

export const WhiteLabel = styled.span`
  color: white;
`;

export const TextTitle = styled.div<{ size?: "s" | "m" | "l" | "xl"; number?: boolean; disabled?: boolean }>`
  font-size: ${({ size }) =>
    size === "s" ? "18px" : size === undefined || size === "m" ? "24px" : size === "l" ? "28px" : "30px"};
  font-family: ${({ number }) => (!number ? PLUS_JAKARTA_SANS : SPACE_MONO)};
  color: ${({ disabled }) => (disabled ? transparentWhite : white)};
  line-height: 1;
  font-weight: 700;
`;

export const TextDescription = styled.div<{ number?: boolean }>`
  font-size: 14px;
  font-family: ${({ number }) => (!number ? PLUS_JAKARTA_SANS : SPACE_MONO)};
  color: ${transparentWhite};
`;

export type PageSizes = {
  s: ["5", "10", "20"];
  m: ["10", "20", "30"];
  l: ["20", "50", "100"];
};

export const PAGE_SIZE_OPTIONS: PageSizes = {
  s: ["5", "10", "20"],
  m: ["10", "20", "30"],
  l: ["20", "50", "100"],
};
